import React, { useState, useEffect } from "react";
import { Col, Container, Row, Form } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";
import "../../css/product-details.css";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
} from "@mui/material";

export default function ProductDetails() {
  const location = useLocation();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { product } = location.state || {};
  const variants = product?.variants || [];
  const [selectedCover, setSelectedCover] = useState(product?.covers?.[0]);

  // Feature section: which tab (Health / Sustainability / Quality / etc.) is active
  const [activeFeatureTab, setActiveFeatureTab] = useState(0);

  useEffect(() => {
    if (product?.covers?.length) {
      setSelectedCover(product.covers[0]);
    }
  }, [product]);

  if (!product) {
    return <div>No product details available.</div>;
  }

  const isPillow =
    product.title.toLowerCase().includes("pillow") ||
    product.subcategory.toLowerCase().includes("pillow");

  const handleCoverChange = (event) => {
    const coverId = event.target.value;
    setSelectedCover(product.covers[coverId]);
  };

  const groupedVariants = variants.reduce((acc, variant) => {
    const key = isPillow ? variant.size : variant.thickness;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(variant);
    return acc;
  }, {});

  const flattenedData = Object.keys(groupedVariants).flatMap((key) =>
    groupedVariants[key].map((variant) => ({
      key,
      size: variant.size || "",
      dimensions: [variant.dimension_cm, variant.dimension_in].join(", "),
      discountPercentage: variant.discountPercentage,
      price: variant.price,
      id: variant._id,
    }))
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedData = flattenedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const featureSection = product.featureSection;
  const layerSection = product.layerSection;
  const mattressTypeInfo = product.mattressTypeInfo;

  // Only show a field if it actually has a value for this product.
  // Handles null/undefined, empty strings, and empty arrays — so fields
  // that don't apply to a given subcategory (mattress vs pillow vs
  // bedsheet) simply don't render instead of showing "N/A".
  const hasVal = (v) => {
    if (v === null || v === undefined) return false;
    if (typeof v === "string" && v.trim() === "") return false;
    if (Array.isArray(v) && v.length === 0) return false;
    return true;
  };

  return (
    <div>
      <div className="product-details">
        <Container fluid>
          <div className="product-head-data">
            <div className="d-flex gap-2">
              <h5>
                <Link to="/">Products</Link>
              </h5>
              <span>/</span>
              <span>Product Details</span>
            </div>
            <h4>{product.title}</h4>
            <hr />
          </div>
          <div className="h-[75vh] overflow-y-auto overflow-x-hidden">
            <div className="product-data">
              <Row>
                <Col lg={7} className="prodInfo me-3">
                  <h5>Product Information</h5>
                  <hr />
                  <form>
                    <Form.Group className="mt-2">
                      <Form.Label>Title</Form.Label>
                      <Form.Control
                        type="text"
                        disabled
                        value={product.title}
                        readOnly
                      />
                    </Form.Group>
                    {(hasVal(product.seriesName) || hasVal(product.tagline)) && (
                      <div className="row mt-2">
                        {hasVal(product.seriesName) && (
                          <Form.Group as={Col} md="6">
                            <Form.Label>Series Name</Form.Label>
                            <Form.Control
                              type="text"
                              value={product.seriesName}
                              disabled
                              readOnly
                            />
                          </Form.Group>
                        )}
                        {hasVal(product.tagline) && (
                          <Form.Group as={Col} md="6">
                            <Form.Label>Tagline</Form.Label>
                            <Form.Control
                              type="text"
                              value={product.tagline}
                              disabled
                              readOnly
                            />
                          </Form.Group>
                        )}
                      </div>
                    )}
                    <div className="row mt-2">
                      <Form.Group as={Col} md="6">
                        <Form.Label>Category</Form.Label>
                        <Form.Control
                          type="text"
                          value={product.category}
                          disabled
                          readOnly
                        />
                      </Form.Group>
                      <Form.Group as={Col} md="6">
                        <Form.Label>Subcategory</Form.Label>
                        <Form.Control
                          type="text"
                          value={product.subcategory}
                          readOnly
                          disabled
                        />
                      </Form.Group>
                    </div>
                    <div className="row mt-2">
                      <Form.Group as={Col} md="6">
                        <Form.Label>Product SKU</Form.Label>
                        <Form.Control
                          type="text"
                          disabled
                          value={product._id}
                          readOnly
                        />
                      </Form.Group>
                      {hasVal(product.sku) && (
                        <Form.Group as={Col} md="6">
                          <Form.Label>SKU Code</Form.Label>
                          <Form.Control
                            type="text"
                            disabled
                            value={product.sku}
                            readOnly
                          />
                        </Form.Group>
                      )}
                      {hasVal(product.isHidden) && (
                        <Form.Group as={Col} md="6">
                          <Form.Label>Hidden</Form.Label>
                          <Form.Control
                            type="text"
                            disabled
                            value={product.isHidden ? "Yes" : "No"}
                            readOnly
                          />
                        </Form.Group>
                      )}
                    </div>
                    {(hasVal(product.weight) ||
                      hasVal(product.quantity) ||
                      hasVal(product.averageRating) ||
                      hasVal(product.rating)) && (
                      <div className="row mt-2">
                        {hasVal(product.weight) && (
                          <Form.Group as={Col} md="3">
                            <Form.Label>Weight</Form.Label>
                            <Form.Control
                              type="text"
                              value={product.weight}
                              readOnly
                              disabled
                            />
                          </Form.Group>
                        )}
                        {hasVal(product.quantity) && (
                          <Form.Group as={Col} md="3">
                            <Form.Label>Quantity</Form.Label>
                            <Form.Control
                              type="text"
                              value={product.quantity}
                              readOnly
                              disabled
                            />
                          </Form.Group>
                        )}
                        {hasVal(product.averageRating) && (
                          <Form.Group as={Col} md="3">
                            <Form.Label>Avg. Rating</Form.Label>
                            <Form.Control
                              type="text"
                              value={product.averageRating}
                              readOnly
                              disabled
                            />
                          </Form.Group>
                        )}
                        {hasVal(product.rating) && (
                          <Form.Group as={Col} md="3">
                            <Form.Label>Rating Count</Form.Label>
                            <Form.Control
                              type="text"
                              value={product.rating}
                              readOnly
                              disabled
                            />
                          </Form.Group>
                        )}
                      </div>
                    )}
                    {(hasVal(product.firmness) || hasVal(product.material)) && (
                      <div className="row mt-2">
                        {hasVal(product.firmness) && (
                          <Form.Group as={Col} md="6">
                            <Form.Label>Firmness</Form.Label>
                            <Form.Control
                              type="text"
                              value={
                                Array.isArray(product.firmness)
                                  ? product.firmness.join(", ")
                                  : product.firmness
                              }
                              readOnly
                              disabled
                            />
                          </Form.Group>
                        )}
                        {hasVal(product.material) && (
                          <Form.Group as={Col} md="6">
                            <Form.Label>Material</Form.Label>
                            <Form.Control
                              type="text"
                              value={
                                Array.isArray(product.material)
                                  ? product.material.join(", ")
                                  : product.material
                              }
                              readOnly
                              disabled
                            />
                          </Form.Group>
                        )}
                      </div>
                    )}
                    {mattressTypeInfo &&
                      (hasVal(mattressTypeInfo.type) ||
                        hasVal(mattressTypeInfo.tagline)) && (
                        <div className="row mt-2">
                          {hasVal(mattressTypeInfo.type) && (
                            <Form.Group as={Col} md="6">
                              <Form.Label>Mattress Type</Form.Label>
                              <Form.Control
                                type="text"
                                value={mattressTypeInfo.type}
                                readOnly
                                disabled
                              />
                            </Form.Group>
                          )}
                          {hasVal(mattressTypeInfo.tagline) && (
                            <Form.Group as={Col} md="6">
                              <Form.Label>Type Tagline</Form.Label>
                              <Form.Control
                                type="text"
                                value={mattressTypeInfo.tagline}
                                readOnly
                                disabled
                              />
                            </Form.Group>
                          )}
                        </div>
                      )}
                    {hasVal(product.bedsheetType) && (
                      <div className="row mt-2">
                        <Form.Group as={Col} md="6">
                          <Form.Label>Bedsheet Type</Form.Label>
                          <Form.Control
                            type="text"
                            value={product.bedsheetType}
                            readOnly
                            disabled
                          />
                        </Form.Group>
                      </div>
                    )}
                    {hasVal(product.bedsheetColors) && (
                      <div className="mt-3">
                        <Form.Label>Bedsheet Colors</Form.Label>
                        <div
                          className="d-flex mt-2"
                          style={{ gap: "16px", flexWrap: "wrap" }}
                        >
                          {product.bedsheetColors.map((color) => (
                            <div
                              key={color._id || color.colorName}
                              style={{
                                border: "1px solid #e0e0e0",
                                borderRadius: "10px",
                                padding: "10px",
                                width: "140px",
                                textAlign: "center",
                              }}
                            >
                              <div
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  borderRadius: "50%",
                                  backgroundColor: color.colorCode || "#fff",
                                  border: "1px solid #ccc",
                                  margin: "0 auto 8px",
                                }}
                                title={color.colorCode}
                              />
                              <div style={{ fontSize: "13px", fontWeight: "bold" }}>
                                {color.colorName}
                              </div>
                              <div style={{ fontSize: "11px", color: "#888" }}>
                                {color.colorCode}
                              </div>
                              {Array.isArray(color.images) &&
                                color.images.length > 0 && (
                                  <div
                                    className="d-flex mt-2"
                                    style={{ gap: "4px", flexWrap: "wrap", justifyContent: "center" }}
                                  >
                                    {color.images.map((img, i) => (
                                      <img
                                        key={i}
                                        src={img}
                                        alt={`${color.colorName} ${i + 1}`}
                                        style={{
                                          width: "30px",
                                          height: "30px",
                                          objectFit: "cover",
                                          borderRadius: "4px",
                                        }}
                                      />
                                    ))}
                                  </div>
                                )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <Form.Group className="mt-2">
                      <Form.Label>Description</Form.Label>
                      <Form.Control
                        as="textarea"
                        value={product.description}
                        readOnly
                        disabled
                        className="description-textarea"
                        style={{
                          height: "150px",
                          whiteSpace: "pre-wrap",
                          paddingTop: "10px",
                          lineHeight: "20px",
                        }}
                      />
                    </Form.Group>
                  </form>
                  <Row className="mt-3">
                    <Col lg={12} className="prodInfo me-3">
                      <h5>Media</h5>
                      <hr />
                      <Row>
                        {product.imageurl.map((image, index) => (
                          <Col
                            lg={4}
                            key={index}
                            className="text-center mb-4 mt-2"
                          >
                            <img
                              src={image}
                              alt={`Product Media ${index + 1}`}
                              style={{
                                width: "100%",
                                height: "100%",
                                borderRadius: "10px",
                              }}
                            />
                          </Col>
                        ))}
                      </Row>
                    </Col>
                  </Row>
                </Col>
                <Col lg={4} className="prodInfo">
                  <h5>Offers</h5>
                  <hr />
                  <Form.Group className="mt-3">
                    <Form.Control
                      as="textarea"
                      value={product.offerdescription || "No offer available"}
                      readOnly
                      disabled
                      style={{
                        height: "80px",
                        whiteSpace: "pre-wrap",
                        paddingTop: "10px",
                        lineHeight: "20px",
                      }}
                    />
                  </Form.Group>
                  <Form.Group className="mt-3 mb-3">
                    <Form.Control
                      value={product.offercode || "No offer code"}
                      readOnly
                      disabled
                    />
                  </Form.Group>
                  {product.covers.length !== 0 && (
                    <div>
                      <h5>Cover Information</h5>
                      <hr />
                      <Form.Group>
                        <div className="dropdown-container">
                          <Form.Control
                            as="select"
                            onChange={handleCoverChange}
                            defaultValue="0"
                          >
                            {product.covers.map((cover, index) => (
                              <option key={cover._id} value={index}>
                                {cover.name}
                              </option>
                            ))}
                          </Form.Control>
                          <FontAwesomeIcon
                            icon={faChevronDown}
                            className="dropdown-icon"
                          />
                        </div>
                      </Form.Group>
                      <div className="cover-details mt-4">
                        <Form.Group className="mt-3">
                          <Form.Label>Cover Name</Form.Label>
                          <Form.Control
                            type="text"
                            value={selectedCover?.name || ""}
                            readOnly
                            disabled
                          />
                        </Form.Group>
                        <Form.Group className="mt-3">
                          <Form.Label>Cover Note</Form.Label>
                          <Form.Control
                            type="text"
                            value={selectedCover?.notes || ""}
                            readOnly
                            disabled
                          />
                        </Form.Group>
                        <Form.Group className="mt-4">
                          <Form.Label>Description</Form.Label>
                          <Form.Control
                            as="textarea"
                            value={selectedCover?.description || ""}
                            readOnly
                            disabled
                            style={{
                              height: "125px",
                              whiteSpace: "pre-wrap",
                              paddingTop: "10px",
                              lineHeight: "20px",
                            }}
                          />
                        </Form.Group>
                        {selectedCover?.image && (
                          <img
                            src={selectedCover.image}
                            alt={selectedCover.name}
                            style={{
                              width: "100%",
                              borderRadius: "10px",
                              marginTop: "20px",
                            }}
                          />
                        )}
                      </div>
                    </div>
                  )}
                </Col>
              </Row>
            </div>

            {/* ---------------- Layers Section ---------------- */}
            {layerSection && layerSection.layers?.length > 0 && (
              <Row className="mt-5">
                <Col lg={11} className="prodInfo">
                  <h4>Layers</h4>
                  <hr />
                  {layerSection.mainTitle && (
                    <h5 className="mt-2">{layerSection.mainTitle}</h5>
                  )}
                  {layerSection.mainDescription && (
                    <p className="text-muted">{layerSection.mainDescription}</p>
                  )}
                  <Row className="mt-3">
                    {(layerSection.layers || [])
                      .slice()
                      .sort((a, b) => (a.layerNumber || 0) - (b.layerNumber || 0))
                      .map((layer) => (
                        <Col
                          lg={4}
                          md={6}
                          key={layer._id}
                          className="mb-4"
                        >
                          <div
                            style={{
                              border: "1px solid #e0e0e0",
                              borderRadius: "10px",
                              padding: "15px",
                              height: "100%",
                            }}
                          >
                            {layer.image && (
                              <img
                                src={layer.image}
                                alt={layer.title}
                                style={{
                                  width: "100%",
                                  borderRadius: "8px",
                                  marginBottom: "10px",
                                }}
                              />
                            )}
                            <div
                              style={{
                                fontSize: "13px",
                                fontWeight: "bold",
                                color: "#888",
                              }}
                            >
                              LAYER {layer.layerNumber}
                            </div>
                            <div
                              style={{
                                fontSize: "16px",
                                fontWeight: "bold",
                                marginTop: "4px",
                              }}
                            >
                              {layer.title}
                            </div>
                            <div
                              style={{
                                fontSize: "14px",
                                fontStyle: "italic",
                                color: "#555",
                                marginBottom: "8px",
                              }}
                            >
                              {layer.subtitle}
                            </div>
                            <div style={{ fontSize: "14px", whiteSpace: "pre-wrap" }}>
                              {layer.description}
                            </div>
                          </div>
                        </Col>
                      ))}
                  </Row>
                </Col>
              </Row>
            )}

            {/* ---------------- Features Section ---------------- */}
            {featureSection && (
              <Row className="mt-5">
                <Col lg={11} className="prodInfo">
                  <h4>Features</h4>
                  <hr />
                  {featureSection.mainTitle && (
                    <h5 className="mt-2">{featureSection.mainTitle}</h5>
                  )}
                  {featureSection.mainDescription && (
                    <p className="text-muted">
                      {featureSection.mainDescription}
                    </p>
                  )}

                  {(featureSection.backgroundImage ||
                    featureSection.mobileBackgroundImage) && (
                    <div className="row mt-2 mb-3">
                      {featureSection.backgroundImage && (
                        <Col lg={6}>
                          <Form.Label>Desktop Background</Form.Label>
                          <img
                            src={featureSection.backgroundImage}
                            alt="Feature background"
                            style={{ width: "100%", borderRadius: "10px" }}
                          />
                        </Col>
                      )}
                      {featureSection.mobileBackgroundImage && (
                        <Col lg={6}>
                          <Form.Label>Mobile Background</Form.Label>
                          <img
                            src={featureSection.mobileBackgroundImage}
                            alt="Feature mobile background"
                            style={{ width: "100%", borderRadius: "10px" }}
                          />
                        </Col>
                      )}
                    </div>
                  )}

                  {/* Tab selector */}
                  <div className="d-flex gap-2 mt-3 mb-3" style={{ flexWrap: "wrap" }}>
                    {(featureSection.tabs || []).map((tab, index) => (
                      <button
                        type="button"
                        key={tab._id}
                        onClick={() => setActiveFeatureTab(index)}
                        style={{
                          padding: "8px 16px",
                          borderRadius: "20px",
                          border:
                            activeFeatureTab === index
                              ? "1px solid #333"
                              : "1px solid #ccc",
                          backgroundColor:
                            activeFeatureTab === index ? "#333" : "#fff",
                          color: activeFeatureTab === index ? "#fff" : "#333",
                          fontWeight: "bold",
                          cursor: "pointer",
                        }}
                      >
                        {tab.tabTitle}
                      </button>
                    ))}
                  </div>

                  {featureSection.tabs?.[activeFeatureTab] && (
                    <div>
                      <h6 style={{ color: "#555" }}>
                        {featureSection.tabs[activeFeatureTab].tabSubtitle}
                      </h6>
                      <Row className="mt-3">
                        {(
                          featureSection.tabs[activeFeatureTab].items || []
                        ).map((item) => (
                          <Col lg={3} md={6} key={item._id} className="mb-4">
                            <div
                              style={{
                                border: "1px solid #e0e0e0",
                                borderRadius: "10px",
                                padding: "15px",
                                height: "100%",
                                textAlign: "center",
                              }}
                            >
                              {item.icon && (
                                <img
                                  src={item.icon}
                                  alt={item.title}
                                  style={{
                                    width: "48px",
                                    height: "48px",
                                    objectFit: "contain",
                                    marginBottom: "10px",
                                  }}
                                />
                              )}
                              <div
                                style={{
                                  fontSize: "15px",
                                  fontWeight: "bold",
                                  marginBottom: "6px",
                                }}
                              >
                                {item.title}
                              </div>
                              <div
                                style={{
                                  fontSize: "13px",
                                  color: "#555",
                                  whiteSpace: "pre-wrap",
                                }}
                              >
                                {item.description}
                              </div>
                            </div>
                          </Col>
                        ))}
                      </Row>
                    </div>
                  )}
                </Col>
              </Row>
            )}

            {/* ---------------- Variants Section ---------------- */}
            <Row className="mt-5 mb-5">
              <Col
                lg={7}
                className="prodInfo sticky top-0"
                style={{
                  position: "sticky !important",
                  top: "0px",
                }}
              >
                <h4>Variants</h4>
                <hr />
                <Row className="prodInfo">
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead style={{ position: "static", top: "0" }}>
                        <TableRow>
                          <TableCell
                            style={{ fontSize: "20px", fontWeight: "bold" }}
                          >
                            #
                          </TableCell>
                          {!isPillow && (
                            <TableCell
                              style={{ fontSize: "20px", fontWeight: "bold" }}
                            >
                              Thickness
                            </TableCell>
                          )}
                          <TableCell
                            style={{ fontSize: "20px", fontWeight: "bold" }}
                          >
                            Size
                          </TableCell>
                          <TableCell
                            style={{ fontSize: "20px", fontWeight: "bold" }}
                          >
                            Dimensions
                          </TableCell>
                          <TableCell
                            style={{ fontSize: "20px", fontWeight: "bold" }}
                          >
                            Discount Percentage
                          </TableCell>
                          <TableCell
                            style={{ fontSize: "20px", fontWeight: "bold" }}
                          >
                            Price
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedData.map((row, index) => (
                          <TableRow
                            key={row.id}
                            sx={{
                              borderBottom: "1px solid gray",
                              backgroundColor:
                                index % 2 === 0 ? "#f2f2f2" : "#ffffff",
                            }}
                          >
                            <TableCell>
                              {index + 1 + page * rowsPerPage}.
                            </TableCell>
                            {!isPillow && (
                              <TableCell>{`${row.key} inches`}</TableCell>
                            )}
                            <TableCell>{row.size}</TableCell>
                            <TableCell>{row.dimensions}</TableCell>
                            <TableCell>{row.discountPercentage || 0}</TableCell>
                            <TableCell style={{ fontWeight: "bold" }}>
                              {row.price.toLocaleString("en-IN", {
                                style: "currency",
                                currency: "INR",
                              })}{" "}
                              /-
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <TablePagination
                      component="div"
                      count={flattenedData.length}
                      page={page}
                      onPageChange={handleChangePage}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      rowsPerPageOptions={[10, 15, 25]}
                    />
                  </TableContainer>
                </Row>
              </Col>
            </Row>
          </div>
        </Container>
      </div>
    </div>
  );
}